import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[48px] items-center">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-1">
                {/* MIMPS-01: BlackVoxel wordmark fallback instead of the upstream OHIF logo.
                    MOB-02 (V4): mark-only logo below lg — the 232px wordmark ate
                    most of a narrow header. (Was md: — that left a 768-1024px gap
                    where this showed full-size while Secondary was still hidden
                    below lg, so nothing agreed on a single collapse point.) */}
                {WhiteLabeling?.createLogoComponentFn?.(React, props) || (
                  <>
                    <img
                      src="/blackvoxel-mark.svg"
                      alt="BlackVoxel Viewer"
                      className="h-6 w-6 lg:hidden"
                    />
                    <img
                      src="/blackvoxel-logo.svg"
                      alt="BlackVoxel Viewer"
                      className="hidden h-[22px] w-[232px] lg:block"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          {/* MOB-02 (V4): the secondary toolbar is pinned at left-[250px] — every
              other block in this header now also collapses below lg (see the
              logo + right-cluster comments), so lg is the single agreed collapse
              point and nothing overlaps it anymore. */}
          <div className="absolute top-1/2 left-[250px] hidden h-8 -translate-y-1/2 lg:block">
            {Secondary}
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <div className="flex items-center justify-center space-x-2">{children}</div>
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center">
            {/* MOB-02 (V4): undo/redo and patient info are desktop-only (lg+); the
                gear menu stays at all widths (About/Preferences/Logout). Was
                md: — that showed these alongside the full-width logo in the
                768-1024px gap while Secondary stayed hidden, so this cluster
                and the logo would crowd/overlap each other at the same widths. */}
            <div className="hidden lg:flex">{UndoRedo}</div>
            <div className="border-muted mx-1.5 hidden h-[25px] border-r lg:block"></div>
            <div className="hidden lg:block">{PatientInfo}</div>
            <div className="border-muted mx-1.5 hidden h-[25px] border-r lg:block"></div>
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-muted mt-2 h-full w-full"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
